/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2023 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import { createHandlerChainRunner, Handler } from "@insite/client-framework/HandlerCreator";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { AnyAction } from "@insite/client-framework/Store/Reducers";
import { getProductCollectionV1 } from "@stoneage/App/Services/ProductServiceV1/ProductServiceV1";
import {
    getProductComponentList,
    ProductComponentsProps,
    ProductPartList,
    ProductPartListModel,
} from "@stoneage/App/Services/QuickProductLookup/QuickProductLookupService";
import { CustomAppState } from "@stoneage/App/Store/Reducers";
import { setUrlsToQuickLookupProducts } from "@stoneage/App/Widgets/QuickProductLookup/QuickProductLookupHelpers";

interface Props {
    apiParameter: ProductComponentsProps;
    apiResult: {
        successful: boolean;
        partLists: ProductPartListModel | null;
        error?: string;
    };
}

type Parameter = {
    productNumber: string;
    onSuccess?: (result: any) => void;
    onError?: (e: any) => void;
};

type HandlerType = Handler<Parameter, Props>;

export const BeginLoadingProducts: HandlerType = props => {
    props.dispatch({
        type: "Pages/ProductLookip/SetIsLoadingProductPartList",
        productNumber: props.parameter.productNumber,
    } as unknown as AnyAction);
};

export const PopulateApiParameters: HandlerType = props => {
    props.apiParameter = { ...props.parameter };
};

export const RequestDataFromApi: HandlerType = async props => {
    props.apiResult = await getProductComponentList(props.apiParameter);
};

export const onSuccess: HandlerType = async props => {
    if (props.apiResult && props.apiResult.successful && props.apiResult.partLists) {
        const { productNumber } = props.parameter;
        let currentPartLists = (props.getState() as CustomAppState).xProductLookup.productPartList;
        const defaultPageSize = getSettingsCollection(props.getState())?.websiteSettings.defaultPageSize;
        const { paginatedProducts } = await buildProductsPagination(
            props.apiResult.partLists?.dealerComponentPrices,
            defaultPageSize,
        );
        if (!currentPartLists?.[productNumber]?.length) {
            const productPartListData = {} as any;
            productPartListData[productNumber] = paginatedProducts;
            currentPartLists = {
                ...currentPartLists,
                ...productPartListData,
            };
        }
        props.dispatch({
            type: "Pages/ProductLookup/SetProductPartList",
            productPartList: currentPartLists,
        } as unknown as AnyAction);
        props.parameter.onSuccess?.(props.apiResult);
    }
};

export const OnError: HandlerType = props => {
    if (props.apiResult && !props.apiResult.successful) {
        const currentPartLists = (props.getState() as CustomAppState).xProductLookup.productPartList;
        props.dispatch({
            type: "Pages/ProductLookup/SetProductPartList",
            productPartList: currentPartLists,
        } as unknown as AnyAction);
        props.parameter.onError?.(props.apiResult.error);
    }
};

async function buildProductsPagination(products: ProductPartList[], defaultPageSize: number, page?: number) {
    const _paginatedProducts = [...products];
    const productNumbers =
        _paginatedProducts.filter(product => !product.urlIsSet).map(item => item.productNumber) || [];
    let paginatedProducts = [..._paginatedProducts];
    if (productNumbers.length !== 0) {
        try {
            const { products } = await getProductCollectionV1({ erpNumbers: productNumbers });
            paginatedProducts = setUrlsToQuickLookupProducts(products, _paginatedProducts) as ProductPartList[];
        } catch (error: any) {
            /* empty */
        }
    }

    return {
        paginatedProducts,
    };
}

export const chain = [BeginLoadingProducts, PopulateApiParameters, RequestDataFromApi, onSuccess, OnError];

const getProductPartList = createHandlerChainRunner(chain, "GetProductPartList");
export default getProductPartList;
